/* The hero figure: revision C and revision D, animated.

   Ported from the "Revision Figure animated" design bundle, whose `Stage` ran
   inside a design-tool runtime with React from unpkg. What changed in the
   port is only the plumbing:

   - the shape boxes come from figures.jsx instead of a copy in this file;
   - every word comes from the strings PHP prints, so the figure speaks the
     page's language;
   - the end card's call to action is the hero's own CTA, handed over by
     front-page.php, rather than a hard-coded "Try it yourself";
   - the play and replay controls are styled by story.css (`.figure-control`),
     because an inline style cannot carry :hover;
   - the design's `#t=` hash scrubber is gone. It was a tool for reviewing
     frames, and on this page the hash belongs to the nav's anchors.

   The timeline, the crops and the pointer's target are the design's numbers.
   They are tuned by eye to shape 7 (PICK) on this sheet: a different pair of
   drawings needs new ones, not just new `shapes`.

   One loop is LOOP seconds. In order: the sheets start overlaid with revision
   C tinted blue, every shape is framed in turn, the sheets pull apart, a
   pointer clicks shape 7, both sheets zoom and pan onto it, and an end card
   fades in over the figure before the loop starts again. */
import React from "react";

import { fill, useStrings } from "./strings-context.jsx";

const LOOP = 12.5;
const GAP = 16;
const LABEL_H = 20;
/* The shape the pointer clicks and the close-up lands on, by index. */
const PICK = 6;
/* Where the close-up's two crops sit, in sheet units. */
const CLOSE = { cx: 600, cy: 600, h: 280 };
const CLOSER = { cx: 596, cy: 566, h: 150 };
/* Where the pointer clicks, in sheet units: inside shape 7. */
const TARGET = { x: 610, y: 746 };
/* A time that shows the pulled-apart sheets with every change marked: the
   one still frame a visitor who asked for reduced motion gets. */
const STILL = 5;

const cl = (x, a, b) => Math.max(a, Math.min(b, x));
const seg = (t, a, b) => cl((t - a) / (b - a), 0, 1);
const ease = (x) => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2);
const sine = (x) => -(Math.cos(Math.PI * x) - 1) / 2;
const lerp = (a, b, k) => a + (b - a) * k;
/* Zoom is interpolated in log space so it feels even rather than rushing
   at the end. */
const lerpCrop = (p, q, k) => ({
  cx: lerp(p.cx, q.cx, k),
  cy: lerp(p.cy, q.cy, k),
  h: Math.exp(lerp(Math.log(p.h), Math.log(q.h), k)),
});

const label = {
  font: "var(--type-label)",
  letterSpacing: "var(--tracking-mono-wide)",
  lineHeight: 1,
  color: "var(--paper-dim)",
};

/* Revision C redrawn in the base-revision blue, for the overlaid state. The
   SVG's own linework is paper white; this filter chain lands it on
   --blue-draft. */
const TINT =
  "brightness(0) saturate(100%) invert(49%) sepia(45%) saturate(620%) hue-rotate(172deg) brightness(92%)";

function usePrefersReducedMotion() {
  return React.useMemo(
    () => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    [],
  );
}

function Stage({ figure, paused, replayKey, cta, reduced }) {
  const fig = useStrings().figure;
  const { width: W, height: H, shapes } = figure;

  const root = React.useRef(null);
  const [w, setW] = React.useState(976);
  const [t, setT] = React.useState(reduced ? STILL : 0);
  const clock = React.useRef({ T: reduced ? STILL : 0, loops: 0 });
  const visible = React.useRef(true);

  React.useEffect(() => {
    const el = root.current;
    if (!el) return;
    setW(el.clientWidth || 976);
    const ro = new ResizeObserver(() => setW(el.clientWidth || 976));
    ro.observe(el);
    /* Off screen, the clock stops rather than running on unseen, so a visitor
       scrolling back up finds the figure where they left it. */
    const io = new IntersectionObserver((es) => { visible.current = es[0].isIntersecting; }, { threshold: 0.1 });
    io.observe(el);
    return () => { ro.disconnect(); io.disconnect(); };
  }, []);

  React.useEffect(() => {
    clock.current = { T: reduced ? STILL : 0, loops: 0 };
    setT(clock.current.T);
  }, [replayKey, reduced]);

  React.useEffect(() => {
    if (paused || reduced) return;
    let raf;
    let last = performance.now();
    const tick = (now) => {
      const dt = Math.min(0.1, (now - last) / 1000);
      last = now;
      if (visible.current) {
        const c = clock.current;
        c.T += dt;
        if (c.T >= LOOP) { c.T -= LOOP; c.loops += 1; }
        setT(c.T);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [paused, reduced]);

  const loops = clock.current.loops;
  const colW = Math.max(0, (w - GAP) / 2);

  const join = ease(seg(t, 1.4, 2.2));
  const split = ease(seg(t, 3.7, 4.5));
  const sep = t < 3 ? 1 - join : split;
  const marks = seg(t, 2.3, 2.8);
  const zoom = ease(seg(t, 5.9, 6.6));
  const pan = sine(seg(t, 6.6, 8.5));
  const whole = { cx: W / 2, cy: H / 2, h: H };
  const crop = lerpCrop(lerpCrop(whole, CLOSE, zoom), CLOSER, pan);
  const vw = (crop.h * W) / H;
  const vb = [crop.cx - vw / 2, crop.cy - crop.h / 2, vw, crop.h];
  const ppu = colW / vw;
  const frameOp = 1 - seg(t, 5.9, 6.3);
  const closeup = t >= 5.9;
  const picked = t >= 5.55;
  /* No card without a CTA to put on it: an empty blue panel over the figure
     would only hide the drawing. */
  const end = !cta ? 0 : t < 1 ? (loops > 0 ? 1 - seg(t, 0, 0.35) : 0) : seg(t, 8.6, 9.0);

  const colCss = `calc((100% - ${GAP}px) / 2)`;
  const xCss = (k) => `calc((100% + ${GAP}px) * ${k.toFixed(5)})`;
  const ax = xCss(0.25 * (1 - sep));
  const bx = xCss(0.25 + 0.25 * sep);

  const aLabel = `${fig.revision} ${figure.aLetter}`;
  const bLabel = `${fig.revision} ${figure.bLetter}`;
  const pickN = PICK + 1;

  const frames = (side, op) =>
    shapes.map((s, i) => {
      const r = s[side];
      const p0 = 2.3 + i * 0.18;
      const pulse = Math.sin(seg(t, p0, p0 + 0.5) * Math.PI);
      const fillOp = i === PICK && picked ? 0.2 : pulse * 0.28;
      const tag = 14 / ppu;
      return (
        <g key={side + s.id} opacity={op * frameOp * seg(t, p0, p0 + 0.15)}>
          <rect x={r[0]} y={r[1]} width={r[2]} height={r[3]} fill={`rgba(228,87,46,${fillOp})`}
            stroke="var(--redline-500)" strokeWidth={1.2} vectorEffect="non-scaling-stroke" />
          <rect x={r[0]} y={r[1]} width={tag} height={tag} fill="var(--redline-500)" />
          <text x={r[0] + tag / 2} y={r[1] + tag / 2} fill="var(--paper)" fontSize={9 / ppu}
            fontFamily="var(--font-mono)" textAnchor="middle" dominantBaseline="central">{i + 1}</text>
        </g>
      );
    });

  const layerBox = {
    position: "absolute",
    left: `${(-vb[0] / vw) * 100}%`,
    top: `${(-vb[1] / crop.h) * 100}%`,
    width: `${(W / vw) * 100}%`,
    height: `${(H / crop.h) * 100}%`,
    display: "block",
    maxWidth: "none",
    pointerEvents: "none",
  };
  const img = (src, op, extra) => (
    <img src={src} alt="" draggable={false} decoding="async" style={{ ...layerBox, opacity: op, ...extra }} />
  );

  const sheet = (side, x, z) => {
    const isA = side === "a";
    const markOp = isA ? marks * sep : marks;
    return (
      <div style={{
        position: "absolute", left: x, top: LABEL_H, width: colCss, aspectRatio: `${W} / ${H}`, zIndex: z,
        border: "var(--border-width) solid var(--line-on-dark)", background: isA ? "var(--blue-800)" : "transparent",
        overflow: "hidden", boxSizing: "border-box",
      }}>
        {isA && sep < 1 ? img(figure.a, 1 - sep, { filter: TINT }) : null}
        {isA ? (sep > 0 ? img(figure.a, sep) : null) : img(figure.b, 1)}
        {markOp > 0 ? img(isA ? figure.aChanges : figure.bChanges, markOp) : null}
        <svg viewBox={vb.join(" ")} preserveAspectRatio="xMidYMid meet"
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", display: "block" }}>
          {frames(side, isA ? sep : 1)}
        </svg>
      </div>
    );
  };

  const tx = (TARGET.x / W) * 100;
  const ty = (TARGET.y / H) * 100;
  const pp = ease(seg(t, 4.6, 5.45));
  const pOp = t < 4.5 ? 0 : Math.min(seg(t, 4.5, 4.7), 1 - seg(t, 5.9, 6.2));
  const press = t > 5.47 && t < 5.65 ? 0.88 : 1;
  const rt = seg(t, 5.55, 6.1);
  const ringR = 6 + 22 * ease(rt);

  const pointer = pOp > 0 ? (
    <svg width={18} height={22} viewBox="0 0 18 22" aria-hidden="true" style={{
      position: "absolute", left: `${lerp(96, tx, pp)}%`, top: `${lerp(104, ty, pp)}%`, marginLeft: -2, marginTop: -1,
      zIndex: 5, opacity: pOp, transform: `scale(${press})`, transformOrigin: "2px 1px", pointerEvents: "none", overflow: "visible",
    }}>
      <path d="M2 1 L2 17 L6.2 13.2 L9 19.5 L11.6 18.4 L8.9 12.2 L14.5 12.2 Z" fill="var(--paper)"
        stroke="var(--blue-900)" strokeWidth={1.2} strokeLinejoin="miter" />
    </svg>
  ) : null;

  const ring = rt > 0 && rt < 1 ? (
    <div style={{ position: "absolute", zIndex: 4, left: `${tx}%`, top: `${ty}%`, width: 0, height: 0, pointerEvents: "none" }}>
      <div style={{
        position: "absolute", left: -ringR, top: -ringR, width: 2 * ringR, height: 2 * ringR, borderRadius: "50%",
        border: "1px solid var(--redline-500)", opacity: 1 - rt, boxSizing: "border-box",
      }} />
    </div>
  ) : null;

  /* The card sits outside the role="img" group: a link inside an image is
     not a link to a screen reader. While it is faded out it is also out of
     the tab order, so a keyboard never lands on something it cannot see. */
  const shown = end > 0.5;
  const card = end > 0 ? (
    <div aria-hidden={!shown} style={{
      position: "absolute", inset: 0, zIndex: 6, background: "var(--blue-800)", opacity: end, display: "flex",
      flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "var(--space-6)",
      textAlign: "center", padding: "0 var(--space-6)", pointerEvents: shown ? "auto" : "none",
    }}>
      <a className="btn" href={cta.href} tabIndex={shown ? 0 : -1}>{cta.label}</a>
    </div>
  ) : null;

  const head = (
    <div aria-hidden="true" style={{
      display: "flex", alignItems: "center", justifyContent: "space-between", gap: "var(--space-3)",
      marginTop: "var(--space-3)", minHeight: 24, padding: "var(--space-2) var(--space-3)",
      border: "var(--border-width) solid var(--line-on-dark)", background: "var(--fill-on-dark)", opacity: 1 - end,
    }}>
      {closeup ? (
        <span style={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}>
          <span style={{ ...label, width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center", background: "var(--redline-500)", color: "var(--paper)" }}>{pickN}</span>
          <span style={{ ...label, color: "var(--paper)" }}>{fig.modified}</span>
        </span>
      ) : (
        <span style={{ ...label, color: "var(--paper)" }}>{fill(fig.comparedWith, { a: aLabel, b: bLabel })}</span>
      )}
      <span style={label}>
        {closeup
          ? fill(fig.shapeInBoth, { n: pickN })
          : fill(shapes.length === 1 ? fig.changedOne : fig.changedMany, { n: shapes.length })}
      </span>
    </div>
  );

  /* The outline around the whole figure sits on a wrapper, not on `root`:
     root's width is what the frames' number tags are sized from, and a
     border or padding on it would be counted in. */
  return (
    <div style={{ border: "var(--border-width) solid var(--line-on-dark)", padding: "var(--space-4)" }}>
    <div ref={root} style={{ width: "100%" }}>
      <div style={{ position: "relative", paddingTop: LABEL_H }}>
        <div role="img" aria-label={fill(fig.animationLabel, { a: aLabel, b: bLabel, n: pickN })}>
          <div style={{ width: colCss, aspectRatio: `${W} / ${H}` }} />
          <span aria-hidden="true" style={{ ...label, position: "absolute", left: xCss(0.25), top: 0, width: colCss, display: "flex", gap: "var(--space-3)", opacity: 1 - sep }}>
            <span style={{ color: "var(--blue-draft)" }}>{aLabel}</span>
            <span>·</span>
            <span style={{ color: "var(--paper)" }}>{bLabel}</span>
          </span>
          <span aria-hidden="true" style={{ ...label, position: "absolute", left: 0, top: 0, color: "var(--paper)", opacity: sep }}>{aLabel}</span>
          <span aria-hidden="true" style={{ ...label, position: "absolute", left: xCss(0.5), top: 0, color: "var(--paper)", opacity: sep }}>{bLabel}</span>
          {sheet("a", ax, 1)}
          {sheet("b", bx, 2)}
          <div style={{ position: "absolute", left: xCss(0.5), top: LABEL_H, width: colCss, aspectRatio: `${W} / ${H}`, zIndex: 4, pointerEvents: "none" }}>
            {ring}
            {pointer}
          </div>
        </div>
        {card}
      </div>
      {head}
    </div>
    </div>
  );
}

function RevisionAnimation({ figure, cta }) {
  const fig = useStrings().figure;
  const reduced = usePrefersReducedMotion();
  const [paused, setPaused] = React.useState(false);
  const [replayKey, setReplayKey] = React.useState(0);

  return (
    <>
      <Stage figure={figure} paused={paused} replayKey={replayKey} cta={cta} reduced={reduced} />
      {/* A visitor who asked for reduced motion gets one still frame, so there
          is nothing for these to play or replay. */}
      {reduced ? null : (
        <div style={{ display: "flex", justifyContent: "flex-end", gap: "var(--space-1)", marginTop: "var(--space-2)" }}>
          <button type="button" className="figure-control" onClick={() => setPaused((p) => !p)}>
            {paused ? fig.play : fig.pause}
          </button>
          <button type="button" className="figure-control" onClick={() => { setReplayKey((k) => k + 1); setPaused(false); }}>
            {fig.replay}
          </button>
        </div>
      )}
    </>
  );
}

export { RevisionAnimation };
